import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../../../api";
import chatPlaceholder from "../../../../assets/images/chatPlaceholder.svg";
import { useSocket } from "../../../../Context/SockerContext";
import { useCookies } from "../../../../hooks/useCookies";
import styles from "./matches.module.css";
import { MdClose } from "react-icons/md";
import UpgradeModal from "../upgradenow/upgradenow";
import { getProfileImageUrl } from "../../../../utils/serverUtils";
import { useAppContext } from "../../../../Context/UseContext";
import * as messageActon from "../../../../actions/Message";

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [currentMatches, setCurrentMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [showmodal, setshowmodal] = useState(false);
  const cookies = useCookies();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { socket } = useSocket();
  const dialogRef = useRef(null);
  const [imageURL, setImageURL] = useState("");
  const token = cookies.getCookie("token");
  const hasFetched = useRef(false);
  const { isPremium } = useAppContext();
  const hasFetchedRef = useRef(false);

  const fetchMatches = useCallback(async () => {
    try {
      setLoadingMatches(true);
      const response = await messageActon.getMatches(page);
      const data = await response;
      if (data) {
        setMatches(prevRequests => {
          const uniqueRequests = new Map();
          [...prevRequests, ...data].forEach(req =>
            uniqueRequests.set(req.user_id, req)
          );
          return Array.from(uniqueRequests.values());
        });
        setCurrentMatches(data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMatches(false);
    }
  }, [page]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchMatches();
  }, [fetchMatches]);

  useEffect(() => {
    if (currentMatches?.length < 10) return;

    const lastProfileObserver = new IntersectionObserver(entries => {
      const lastProfile = entries[0];

      if (!lastProfile.isIntersecting) return;

      setPage(p => p + 1);
    });

    const lastEl = document.querySelector(`.profile:last-child`);
    if (lastEl) lastProfileObserver.observe(lastEl);

    return () => {
      lastProfileObserver.disconnect();
    };
  }, [currentMatches]);

  const getConversations = useCallback(() => {
    (async () => {
      const response = await fetch(
        `${API_URL}customer/chat/get-conversations`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setConversations(data);
      }
    })();
  }, [token]);

  const fetchConversations = useCallback(() => {
    socket?.on("fetch-messages", async () => {
      const response = await messageActon.getLastedConversion();
      const result = await response;
      if (result) {
        window.dispatchEvent(
          new CustomEvent("newChatReceived", { detail: result })
        );
        setConversations(prevConversations => [
          result[0],
          ...prevConversations?.filter(
            conversation =>
              conversation?.conversation_id !== result[0]?.conversation_id
          ),
        ]);
      }
    });

    return () => {
      socket?.off("fetch-messages");
    };
  }, [socket]);

  const blockConversions = useCallback(() => {
    socket?.on("block", data => {
      const personId = data.personId;
      setConversations(prev =>
        prev.filter(conversation => conversation.user_id !== personId)
      );
    });

    return () => {
      socket?.off("block");
    };
  }, [socket]);

  useEffect(() => {
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    getConversations();
    fetchConversations();
    blockConversions();
  }, [getConversations, fetchConversations, blockConversions]);

  async function getRoom(userId, name, img) {
    if (isPremium !== true) {
      setshowmodal(true);
      return;
    }

    await chatWith({
      name: name,
      recepientId: userId,
    });
  }

  async function chatWith(payload) {
    try {
      const response = await messageActon.createChatRoom({ participantId: payload?.recepientId });
      if (response.conversationId) {
        if (sessionStorage.getItem("conversationId")) {
          socket?.emit("leave-room", sessionStorage.getItem("conversationId"));
        }
        navigate(`/user/chat/with/${payload?.name}/${payload?.recepientId}`);
      }
    } catch (error) {
      console.error(error);
    }
  }

  function getDateDifference(date) {
    const today = dayjs();
    const messageDate = dayjs(date);
    const diff = today.diff(messageDate, "day");
    const diffInHours = today.diff(messageDate, "hour");
    const diffInMinutes = today.diff(messageDate, "minute");

    if (diff === 0) {
      if (diffInHours < 1) {
        if (diffInMinutes > 1) {
          return `${diffInMinutes} minutes ago`;
        } else if (diffInMinutes === 1) {
          return "A minute ago";
        } else {
          return "Just Now";
        }
      } else {
        return `${diffInHours} hours ago`;
      }
    } else if (diff === 1) {
      return "Yesterday";
    } else {
      return messageDate.format("DD/MM/YYYY");
    }
  }

  return (
    <>
      <p className="h4">Matches</p>

      {Array.isArray(matches) &&
        matches?.length === 0 &&
        Array.isArray(conversations) &&
        conversations?.length === 0 && (
          <div className={styles.chatPlaceholder}>
            <img src={chatPlaceholder} alt="chat placeholder" />
            <p>
              You don't have any chats yet. Make the first move & say hello!
            </p>
          </div>
        )}

      {loadingMatches && (
        <div className={styles.profiles}>
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <div key={i}>
                <Skeleton
                  animation="pulse"
                  variant="circular"
                  width={75}
                  height={75}
                />
              </div>
            ))}
        </div>
      )}

      <div className={styles.profiles}>
        {Array.isArray(matches) &&
          matches.map(match => (
            <div
              key={match.user_id}
              className="profile"
              onClick={() =>
                getRoom(
                  match.user_id,
                  match?.first_name,
                  getProfileImageUrl(match)
                )
              }
            >
              <img
                src={getProfileImageUrl(match)}
                alt="profile"
                width={75}
                height={75}
                style={{
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
              <p>{match?.first_name}</p>
            </div>
          ))}
      </div>

      {conversations?.length !== 0 && (
        <>
          <p className="h4">Messages</p>
          <div className={styles.messagesContainer}>
            {conversations?.map((conversation, i) => (
              <div
                key={i}
                className={styles.message}
                onClick={() =>
                  getRoom(
                    conversation?.user_id,
                    conversation?.first_name,
                    getProfileImageUrl(conversation)
                  )
                }
              >
                <img
                  src={getProfileImageUrl(conversation)}
                  alt="profile"
                  style={{
                    width: "70px",
                    height: "70px",
                    objectFit: "cover",
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    dialogRef.current.showModal();
                    setImageURL(getProfileImageUrl(conversation));
                  }}
                />
                <div>
                  <p>{conversation?.first_name} </p>
                  <p
                    style={{
                      filter: isPremium === true ? "none" : "blur(5px)",
                    }}
                  >
                    {conversation?.message}
                  </p>
                </div>
                <div style={{ flexGrow: "1" }}></div>
                <p>{getDateDifference(conversation.sent_at)}</p>
              </div>
            ))}
          </div>
        </>
      )}
      <dialog ref={dialogRef} className={styles.profileDialog}>
        <MdClose
          size={30}
          color="white"
          onClick={() => dialogRef.current.close()}
          style={{
            position: "absolute",
            right: "1rem",
            top: "1rem",
            cursor: "pointer",
          }}
        />
        <img src={imageURL} alt="profile" />
      </dialog>
      <UpgradeModal show={showmodal} setShow={setshowmodal} />
    </>
  );
}
